/*
 * Confidential and Proprietary.
 * Do not distribute without Bloom Harbor Gifts consent.
 * Copyright Bloom Harbor Gifts 2019. All rights reserved.
 */

import React from 'react';
import { Helmet } from 'react-helmet';
import {
    string, arrayOf, number, shape, object,
} from 'prop-types';
import isRetailOrSale from '../../../../../helpers/isRetailOrSale';
import { sanitizingString } from '../../../../../helpers/Seo/sanitizingString';

// helper function matches (") and replaces with (\")
const mdParser = (answer) => answer?.replace(/"/gi, '\\"');

const brandsWithoutPrefix = {
    // some brands (18F & Berries) need the brand prefix stripped out in the schema.
    1001: true,
    1029: true,
};

const GraphqlProductSeoSchema = ({
    name,
    shortDescription,
    partNumber,
    productSkus,
    location,
    brand,
    reviews,
    productReviews,
}) => {
    const normalizePrefix = (product) => (brandsWithoutPrefix[brand?.id] ? product.id.match(/[^-_]*$/)[0] : product.id);
    const normalizeSku = () => {
        if (productSkus.length === 1) return normalizePrefix(productSkus[0]);
        return partNumber;
    };
    return (
        <Helmet>
            <script type="application/ld+json">
                {`{"@context":"https://schema.org/","@type":"Product",${reviews?.average_score && reviews?.total_reviews
                    ? `"aggregateRating":{"@type":"AggregateRating","ratingValue":"${reviews.average_score}","reviewCount":"${reviews.total_reviews}"},`
                    : ''
                    }${productReviews && productReviews.length
                        ? `"review":[${productReviews.map(
                            (d) => `{"@type":"Review","name":"${d.title}","reviewBody":"${d.content
                                }","reviewRating":{"@type":"Rating","ratingValue":"${d.score
                                }"},"datePublished":"${d.createdAt.substring(
                                    0,
                                    11,
                                )}","author":{"@type":"Person","name":"${d.user.displayName
                                }"}}`,
                        )}],`
                        : ''
                    }"name":"${mdParser(name)}","image":["https:${productSkus[0]?.image?.path}${productSkus[0]?.image?.name
                    }x.jpg"],"description":"${sanitizingString(shortDescription?.trim?.() || '',
                    )}","sku":"${normalizeSku()}","brand":{"@type":"Brand","name":"${brand.description
                    }"},"offers":[${productSkus?.map(
                        (d) => `{"@type":"Offer","url":"https://www.${brand.domain}.com${location.pathname
                            }","priceCurrency":"USD","price":"${isRetailOrSale(
                                d.prices,
                            )}","priceValidUntil":"2026-06-30","sku":"${normalizePrefix(d)
                            }","itemCondition":"https://schema.org/NewCondition","availability":"https://schema.org/InStock","seller":{"@type":"Organization","name":"${brand.description}"}}`
                            )}]}`}
            </script>
        </Helmet>
    );
};

GraphqlProductSeoSchema.propTypes = {
    name: string.isRequired,
    shortDescription: string.isRequired,
    partNumber: string.isRequired,
    productSkus: arrayOf(
        shape({
            id: string.isRequired,
            prices: arrayOf(
                shape({
                    value: number.isRequired,
                }),
            ).isRequired,
        }),
    ).isRequired,
    location: shape({
        pathname: string.isRequired,
    }).isRequired,
    brand: shape({
        domain: 'string',
        identifier: 'string',
        description: 'string',
    }).isRequired,
    reviews: shape({
        average_score: 'string',
        total_reviews: 'string',
    }),
    productReviews: arrayOf(
        shape({
            title: string,
            content: string,
            createdAt: string,
            score: number,
            user: object,
        }),
    ),
};
GraphqlProductSeoSchema.defaultProps = {
    reviews: {},
    productReviews: [],
};
export default GraphqlProductSeoSchema;
